import { Dialog, useDialogRoute } from "@dash/dialog";
import { Button } from "@dash/form";
import { useDeviceType } from "@dash/mq";
import { Page, SectionHeader } from "@dash/page";
import { SearchBox } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import { useProfiles } from "@megarax/features";
import { useEffect, useMemo, useState } from "react";
import { MdAdd } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { Email, getMonths } from "@megaron/serializers";

import { ActionButtons } from "./ActionButtons";
import { AddRegion } from "./AddRegion";
import { MacroregionsTable } from "./MacroregionsTable";
import { MacroregionTile } from "./MacroregionTile";
import { RealizationProgressBar } from "./RealizationProgressBar";
import { ExtendedRegion, RegionsTable } from "./RegionsTable";
import { RegionTile } from "./RegionTile";
import { SaveMacroregion } from "./SaveMacroregion";
import { TimeRangeSelect } from "./TimeRangeSelect";
import { useRegionProfitMetricsFilters } from "./useRegionProfitMetricsFilters";

const normalizeSearchString = (str: string): string => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/[.,\-_\s()[\]{}]/g, "");
};

const matchesSearch = (region: { name: string; ownerEmail: string | null }, searchValue: string): boolean => {
  if (!searchValue.trim()) return true;

  const normalizedSearch = normalizeSearchString(searchValue);
  const normalizedName = normalizeSearchString(region.name);
  const normalizedEmail = normalizeSearchString(region.ownerEmail ?? "");

  return normalizedName.includes(normalizedSearch) || normalizedEmail.includes(normalizedSearch);
};

export const RegionsHome = () => {
  const { isMobile } = useDeviceType();
  const { profiles } = useProfiles();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [searchValue, setSearchValue] = useState("");

  const legacyProfileQuery = useClientManager("auth").userProfilesQuery().useQuery({});

  const legacyProfiles = useMemo(() => legacyProfileQuery.data?.items ?? [], [legacyProfileQuery.data]);

  const [currentMacroregionName, setCurrentMacroregionName] = useState<string | null>(null);

  const navigate = useNavigate();

  const { filters, setFilters } = useRegionProfitMetricsFilters();

  const regionsQuery = useClientManager("crm").regionsQuery().useQuery({});
  const macroregionsQuery = useClientManager("crm").macroregionsQuery().useQuery({});

  const regionProfitMetricsQuery = useClientManager("crm")
    .regionProfitMetricsQuery()
    .useQuery({ months: getMonths(filters.startDate, filters.endDate) });

  useEffect(() => {
    const macroregion = decodeURIComponent(pathname.split("/")[3]);

    if (macroregion) {
      setCurrentMacroregionName(macroregion);
      return;
    }

    setCurrentMacroregionName(null);
  }, [pathname]);

  const currentMacroregion = useMemo(() => {
    if (!currentMacroregionName) {
      return null;
    }

    const macroregion = macroregionsQuery?.data?.items.find((m) => m.name === currentMacroregionName);
    return macroregion ?? null;
  }, [currentMacroregionName, macroregionsQuery?.data?.items]);

  const regionEmailsListSet = new Set(
    regionsQuery.data?.items.map((region) => region.ownerEmail).filter((email) => email !== null),
  ) as Set<Email>;

  const macroregionEmailsListSet = new Set(
    macroregionsQuery.data?.items.flatMap((m) => m.emails).filter((email) => email !== null),
  ) as Set<Email>;

  const regionEmails = Array.from(regionEmailsListSet) as Email[];
  const macroregionEmails = Array.from(macroregionEmailsListSet) as Email[];

  const profilesData = profiles([...regionEmails, ...macroregionEmails]);

  const addRegionDialog = useDialogRoute(`/add-region`, ({ onClose }) => (
    <Dialog
      onClose={onClose}
      header={"Dodaj region"}
      placement={isMobile ? "top" : "center"}
      css={{ width: isMobile ? "100%" : "500px" }}
    >
      <AddRegion
        onSuccess={() => {
          queryClient.invalidateQueries(regionsQuery.key);
          onClose();
        }}
      />
    </Dialog>
  ));

  const addMacroregionDialog = useDialogRoute(`/add-macroregion`, ({ onClose }) => (
    <Dialog
      onClose={onClose}
      header={"Dodaj makroregion"}
      placement={isMobile ? "top" : "center"}
      css={{ width: isMobile ? "100%" : "500px" }}
    >
      <SaveMacroregion
        onSuccess={() => {
          queryClient.invalidateQueries(macroregionsQuery.key);
          onClose();
        }}
      />
    </Dialog>
  ));

  return (
    <>
      <Page>
        <div css={{ display: "flex", gap: "2rem", flexDirection: "column" }}>
          <div
            css={{
              display: "flex",
              alignItems: isMobile ? "left" : "flex-end",
              gap: "2rem",
              justifyContent: "space-between",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <SectionHeader>Regiony i realizacja planu marży</SectionHeader>
            <ActionButtons />
          </div>

          <div
            css={{ display: "flex", flexDirection: "column", gap: "1.75rem", maxWidth: isMobile ? "100%" : "28rem" }}
          >
            <TimeRangeSelect
              startDate={filters.startDate}
              endDate={filters.endDate}
              onDatesChange={(startDate, endDate) => setFilters({ startDate, endDate })}
            />
            <QuerySkeleton query={regionProfitMetricsQuery} height="54px">
              {(realization) => (
                <RealizationProgressBar
                  label="Razem"
                  currentRealization={realization.profit.toNumber()}
                  goalRealization={realization.target.toNumber()}
                />
              )}
            </QuerySkeleton>
          </div>
          <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <SubSectionHeader onAddClick={addRegionDialog.open}>Regiony</SubSectionHeader>
            <SearchBox onChange={setSearchValue} value={searchValue} css={{ width: "100%", flex: "1" }} />
            <QuerySkeleton query={regionsQuery}>
              {(regions) => {
                const extendedRegions: ExtendedRegion[] = regions.items
                  .map((region) => {
                    const profitMetrics = regionProfitMetricsQuery?.data?.byRegion.find(
                      (r) => r.region === region.name,
                    );

                    const legacyUserId = legacyProfiles.find(
                      (profile) => profile.email === region.ownerEmail,
                    )?.legacyUserId;

                    return {
                      region,
                      profile: profilesData?.find((profile) => profile.email === region.ownerEmail),
                      legacyUserId,
                      profitMetrics: {
                        profit: profitMetrics?.profit,
                        target: profitMetrics?.target,
                      },
                    } as const;
                  })
                  .sort((a, b) => {
                    if (!a.profitMetrics.profit || !a.profitMetrics.target || a.profitMetrics.target.eq(0)) {
                      return 1;
                    }

                    if (!b.profitMetrics.profit || !b.profitMetrics.target || b.profitMetrics.target.eq(0)) {
                      return -1;
                    }

                    const percentageA = a.profitMetrics.profit.mul(100).div(a.profitMetrics.target).toDecimalPlaces(2);

                    const percentageB = b.profitMetrics.profit.mul(100).div(b.profitMetrics.target).toDecimalPlaces(2);

                    return percentageB.greaterThan(percentageA) ? 1 : -1;
                  });

                const filteredRegions = extendedRegions.filter((r) => matchesSearch(r.region, searchValue));

                if (isMobile) {
                  return filteredRegions.map((region, i) => (
                    <RegionTile region={region} key={region.region.name} queryKey={regionsQuery.key} />
                  ));
                }
                return <RegionsTable regions={filteredRegions} isLoading={regionsQuery.isLoading} />;
              }}
            </QuerySkeleton>
          </div>
          <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <SubSectionHeader onAddClick={addMacroregionDialog.open}>Makroregiony</SubSectionHeader>
            <QuerySkeleton query={macroregionsQuery}>
              {(macroregions) => {
                const extendedMacroregions = macroregions.items.map((macroregion) => {
                  const profitMetrics = regionProfitMetricsQuery?.data?.byMacroregion.find(
                    (mr) => mr.macroregion === macroregion.name,
                  );

                  return {
                    macroregion,
                    profiles:
                      profilesData?.filter((profile) => macroregion.emails.includes(profile.email as Email)) ?? [],
                    profitMetrics: {
                      profit: profitMetrics?.profit,
                      target: profitMetrics?.target,
                    },
                  } as const;
                });

                if (isMobile) {
                  return extendedMacroregions.map((macroregion) => {
                    return (
                      <MacroregionTile
                        macroregion={macroregion}
                        key={macroregion.macroregion.name}
                        queryKey={macroregionsQuery.key}
                      />
                    );
                  });
                }

                return <MacroregionsTable macroregions={extendedMacroregions} isLoading={false} />;
              }}
            </QuerySkeleton>
          </div>
        </div>
      </Page>
      {addRegionDialog.element}
      {addMacroregionDialog.element}
      {currentMacroregion && (
        <Dialog
          onClose={() => navigate(-1)}
          header={currentMacroregion.name}
          placement={isMobile ? "bottom" : "center"}
          css={{ width: isMobile ? "100%" : "500px" }}
        >
          <SaveMacroregion
            macroregion={currentMacroregion}
            onSuccess={() => {
              queryClient.invalidateQueries(macroregionsQuery.key);
              navigate(-1);
            }}
          />
        </Dialog>
      )}
    </>
  );
};

const SubSectionHeader = ({ children, onAddClick }: { children: React.ReactNode; onAddClick: () => void }) => {
  const theme = useTheme();

  return (
    <div css={{ display: "flex", alignItems: "center", gap: "1em" }}>
      <h3
        css={{
          fontSize: "1.125rem",
          color: theme.colors.primary,
          margin: 0,
          fontFamily: theme.displayFontFamily,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </h3>
      <div css={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
        <Button onClick={onAddClick} icon={<MdAdd />}>
          Nowy
        </Button>
      </div>
    </div>
  );
};
